import { createContext, useContext, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

/**
 * Lets a field show where its value comes from while knowing nothing about inheritance. By context,
 * because the wrapper gets the field as opaque children; a field ignores a slot it does not host.
 */
export interface FieldFallback {
	/** Beside the label, so it costs no height and every wrapped field can show it. */
	annotation?: ReactNode;
	/** The value live elsewhere, drawn inside the empty control. */
	ghost?: ReactNode;
}

const FieldFallbackContext = createContext<FieldFallback | undefined>(undefined);

export const FieldFallbackProvider = FieldFallbackContext.Provider;

export const useFieldFallback = () => useContext(FieldFallbackContext);

/**
 * The caller repeats the control's padding, since `inset-0` resolves against the padding box. Hidden on
 * focus, so the caret never sits in someone else's text.
 */
export function FieldGhost(props: { children: ReactNode; className?: string }) {
	return (
		<div
			className={cn(
				'text-muted-foreground pointer-events-none absolute inset-0 flex items-center text-base peer-focus:hidden md:text-sm',
				props.className,
			)}
		>
			<span className="truncate">{props.children}</span>
		</div>
	);
}
